import { useState, useEffect } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, Check, X } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { formatDateTime } from '../utils/dateTime';
import { DebouncedSearchInput, Spinner, Badge, Button } from '../components/common';
import ResponsiveTable from '../components/common/ResponsiveTable';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import { useClients } from '../hooks/useClients';
import { masterDataService } from '../services/masterDataService';
import api from '../services/api';
import StorageSummaryModal from '../components/inventory/StorageSummaryModal';
import PastGrnUpdateModals from '../components/inventory/PastGrnUpdateModals';
import PastDispatchUpdateModals from '../components/inventory/PastDispatchUpdateModals';
import { grnService } from '../services/grnService';
import { dispatchService } from '../services/dispatchService';
import { StorageSummaryFilters, Client } from '../types';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

type TabType = 'inventory' | 'ledger' | 'pending';

export default function InventoryPage() {
  const { hasPermission, user } = useAuth();
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  
  // Determine active tab from URL, default to 'inventory'
  const activeTab: TabType = (tab as TabType) || 'inventory';
  
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  // Redirect to /inventory/inventory if no tab is specified
  useEffect(() => {
    if (!tab) {
      navigate('/inventory/inventory', { replace: true });
    }
  }, [tab, navigate]);
  
  // Modal states for approvals
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  // Past GRN update row actions (view diff / approve / reject) in the unified table
  const [pastGrnUpdateId, setPastGrnUpdateId] = useState<number | null>(null);
  const [pastGrnAction, setPastGrnAction] = useState<'view' | 'approve' | 'reject' | null>(null);
  // Status sub-filter when viewing the Past GRN type (pending = queue, approved/rejected = history)
  const [pastGrnStatus, setPastGrnStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  // Past Dispatch update row actions + status sub-filter (mirrors Past GRN)
  const [pastDispatchUpdateId, setPastDispatchUpdateId] = useState<number | null>(null);
  const [pastDispatchAction, setPastDispatchAction] = useState<'view' | 'approve' | 'reject' | null>(null);
  const [pastDispatchStatus, setPastDispatchStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');

  // Inventory filters
  const [search, setSearch] = useState('');
  const [inventorySearchOpen, setInventorySearchOpen] = useState(true);
  const [ledgerSearchOpen, setLedgerSearchOpen] = useState(true);
  const [pendingSearchOpen, setPendingSearchOpen] = useState(true);
  const [clientFilter, setClientFilter] = useState<number | undefined>();
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>();
  const [batchNoFilter, setBatchNoFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [showFilters, setShowFilters] = useState(false);

  // Ledger filters
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [showLedgerFilters, setShowLedgerFilters] = useState(false);
  const [ledgerClientFilter, setLedgerClientFilter] = useState<number | undefined>();
  const [ledgerTransactionType, setLedgerTransactionType] = useState<'inward' | 'outward' | undefined>();
  const [ledgerStartDate, setLedgerStartDate] = useState('');
  const [ledgerEndDate, setLedgerEndDate] = useState('');

  // Pending approvals filters
  const [pendingSearch, setPendingSearch] = useState('');
  const [showPendingFilters, setShowPendingFilters] = useState(false);
  const [pendingType, setPendingType] = useState<'grn' | 'dispatch' | 'all' | 'pastgrn' | 'pastdispatch'>('all');
  const [pendingClientFilter, setPendingClientFilter] = useState<number | undefined>();
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');

  // Modals
  const [storageSummaryFilters, setStorageSummaryFilters] = useState<StorageSummaryFilters | null>(null);
  const canApproveGrn = hasPermission('grn:approve');
  const canApproveDispatch = hasPermission('dispatch:approve');
  // Past GRN update approvals are hardcoded to Admin / Super Admin only.
  const isAdmin = ['ADMIN', 'SUPER_ADMIN'].includes((user?.role?.name || '').toUpperCase());
  const canAccessPendingApprovals = canApproveGrn || canApproveDispatch || isAdmin;

  const clearInventoryFilters = () => {
    setSearch('');
    setClientFilter(undefined);
    setCategoryFilter(undefined);
    setBatchNoFilter('');
    setStartDate('');
    setEndDate('');
    setSortOrder('DESC');
  };

  const clearLedgerFilters = () => {
    setLedgerSearch('');
    setLedgerClientFilter(undefined);
    setLedgerTransactionType(undefined);
    setLedgerStartDate('');
    setLedgerEndDate('');
  };

  const clearPendingFilters = () => {
    setPendingSearch('');
    setPendingType('all');
    setPendingClientFilter(undefined);
    setPendingStartDate('');
    setPendingEndDate('');
  };

  // Shared helper: next page based on backend meta (page < totalPages)
  const getNextPageFromMeta = (lastPage: any) => {
    const meta = lastPage?.meta;
    if (!meta) return undefined;
    return meta.page < meta.totalPages ? meta.page + 1 : undefined;
  };

  // Fetch inventory data (infinite scroll)
  const {
    data: inventoryPages,
    isLoading,
    isError,
    error,
    fetchNextPage: fetchNextInventory,
    hasNextPage: hasNextInventory,
    isFetchingNextPage: isFetchingInventory,
  } = useInfiniteQuery({
    queryKey: ['inventory-summary', 'list', { search, clientFilter, categoryFilter, batchNoFilter, startDate, endDate, sortOrder }],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams();
      params.append('sortBy', 'createdAt');
      params.append('sortOrder', sortOrder);
      params.append('page', pageParam.toString());
      if (search) params.append('search', search);
      if (clientFilter) params.append('clientId', clientFilter.toString());
      if (categoryFilter) params.append('productCategoryId', categoryFilter.toString());
      if (batchNoFilter) params.append('batchNo', batchNoFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const { data } = await api.get(`/inventory-summary?${params}`);
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: getNextPageFromMeta,
    enabled: activeTab === 'inventory',
    refetchOnMount: 'always',
    staleTime: 0,
  });
  const inventoryItems = inventoryPages?.pages.flatMap((p) => p.items || []) || [];

  // Fetch transaction ledger (infinite scroll)
  const {
    data: transactionsPages,
    isLoading: transactionsLoading,
    isError: transactionsError,
    fetchNextPage: fetchNextTransactions,
    hasNextPage: hasNextTransactions,
    isFetchingNextPage: isFetchingTransactions,
  } = useInfiniteQuery({
    queryKey: ['inventory-transactions', 'list', { ledgerSearch, ledgerClientFilter, ledgerTransactionType, ledgerStartDate, ledgerEndDate }],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams();
      params.append('page', pageParam.toString());
      if (ledgerSearch) params.append('search', ledgerSearch);
      if (ledgerClientFilter) params.append('clientId', ledgerClientFilter.toString());
      if (ledgerTransactionType) params.append('transactionType', ledgerTransactionType);
      if (ledgerStartDate) params.append('startDate', ledgerStartDate);
      if (ledgerEndDate) params.append('endDate', ledgerEndDate);

      const { data } = await api.get(`/inventory/transactions?${params}`);
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: getNextPageFromMeta,
    enabled: activeTab === 'ledger',
    refetchOnMount: 'always',
    staleTime: 0,
  });
  const transactionItems = transactionsPages?.pages.flatMap((p) => p.items || []) || [];

  // Fetch pending GRNs (infinite scroll)
  const {
    data: grnPages,
    isLoading: pendingGrnsLoading,
    fetchNextPage: fetchNextGrns,
    hasNextPage: hasNextGrns,
    isFetchingNextPage: isFetchingGrns,
  } = useInfiniteQuery({
    queryKey: ['grns', 'submitted', 'list', pendingClientFilter, pendingSearch, pendingStartDate, pendingEndDate],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams();
      params.append('status', 'submitted');
      params.append('page', pageParam.toString());
      if (pendingClientFilter) params.append('clientId', pendingClientFilter.toString());
      if (pendingSearch) params.append('search', pendingSearch);
      if (pendingStartDate) params.append('startDate', pendingStartDate);
      if (pendingEndDate) params.append('endDate', pendingEndDate);

      const { data } = await api.get(`/grn?${params}`);
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: getNextPageFromMeta,
    enabled: activeTab === 'pending' && (pendingType === 'grn' || pendingType === 'all'),
    refetchOnMount: 'always',
    staleTime: 0,
  });
  const pendingGrns = grnPages?.pages.flatMap((p) => p.items || []) || [];

  // Fetch pending dispatches (infinite scroll)
  const {
    data: dispatchPages,
    isLoading: pendingDispatchesLoading,
    fetchNextPage: fetchNextDispatches,
    hasNextPage: hasNextDispatches,
    isFetchingNextPage: isFetchingDispatches,
  } = useInfiniteQuery({
    queryKey: ['dispatches', 'submitted', 'list', pendingClientFilter, pendingSearch, pendingStartDate, pendingEndDate],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams();
      params.append('status', 'submitted');
      params.append('page', pageParam.toString());
      if (pendingClientFilter) params.append('clientId', pendingClientFilter.toString());
      if (pendingSearch) params.append('search', pendingSearch);
      if (pendingStartDate) params.append('startDate', pendingStartDate);
      if (pendingEndDate) params.append('endDate', pendingEndDate);

      const { data } = await api.get(`/dispatch?${params}`);
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: getNextPageFromMeta,
    enabled: activeTab === 'pending' && (pendingType === 'dispatch' || pendingType === 'all'),
    refetchOnMount: 'always',
    staleTime: 0,
  });
  const pendingDispatches = dispatchPages?.pages.flatMap((p) => p.items || []) || [];

  // Fetch Past GRN updates (Admin / Super Admin only). In the "all" view only
  // pending requests are merged in; under the "pastgrn" type the admin can also
  // review processed (approved/rejected) history via the status sub-filter.
  const pastGrnQueryStatus = pendingType === 'pastgrn' ? pastGrnStatus : 'pending';
  const { data: pastGrnUpdatesRaw } = useQuery({
    queryKey: ['past-grn-updates', pastGrnQueryStatus],
    queryFn: () => grnService.listPastGrnUpdates(pastGrnQueryStatus),
    enabled:
      activeTab === 'pending' &&
      isAdmin &&
      (pendingType === 'all' || pendingType === 'pastgrn'),
  });
  // Normalize so shared columns (createdAt) work in the unified table.
  const pendingPastGrn = Array.isArray(pastGrnUpdatesRaw)
    ? pastGrnUpdatesRaw.map((u: any) => ({ ...u, createdAt: u.requestedAt }))
    : [];

  // Fetch Past Dispatch updates (Admin / Super Admin only). Mirrors Past GRN:
  // "all" shows only pending; "pastdispatch" adds the status sub-filter.
  const pastDispatchQueryStatus =
    pendingType === 'pastdispatch' ? pastDispatchStatus : 'pending';
  const { data: pastDispatchUpdatesRaw } = useQuery({
    queryKey: ['past-dispatch-updates', pastDispatchQueryStatus],
    queryFn: () => dispatchService.listPastDispatchUpdates(pastDispatchQueryStatus),
    enabled:
      activeTab === 'pending' &&
      isAdmin &&
      (pendingType === 'all' || pendingType === 'pastdispatch'),
  });
  const pendingPastDispatch = Array.isArray(pastDispatchUpdatesRaw)
    ? pastDispatchUpdatesRaw.map((u: any) => ({ ...u, createdAt: u.requestedAt }))
    : [];

  // Pending tab merges two lists — load the next page of whichever still has more
  const handlePendingLoadMore = () => {
    if (hasNextGrns && !isFetchingGrns) fetchNextGrns();
    if (hasNextDispatches && !isFetchingDispatches) fetchNextDispatches();
  };

  const { data: clientsData } = useClients({ limit: 100, status: ['active'] });
  const { data: categoriesData } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => masterDataService.getProductCategories({ limit: 100, status: 'active' }),
  });

  const handleViewStorage = (item: any) => {
    setStorageSummaryFilters({
      clientId: item.clientId,
      productDescription: item.productDescription,
      batchNo: item.batchNo,
    });
  };

  // Approve GRN mutation
  const approveMutation = useMutation({
    mutationFn: async (grnId: number) => {
      const response = await api.post(`/grn/${grnId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      showToast('GRN approved successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setShowConfirmModal(false);
      setSelectedItem(null);
      setShowDetailsModal(false);
    },
    onError: (error: any) => {
      const message = error?.statusCode === 403
        ? "Access Denied: You don't have permission to approve this GRN. Please contact your administrator if you need access."
        : (error?.message || 'Failed to approve GRN');
      showToast(message, 'error');
    },
  });

  // Reject GRN mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ grnId, rejectionReason }: { grnId: number; rejectionReason: string }) => {
      const response = await api.post(`/grn/${grnId}/reject`, { rejectionReason });
      return response.data;
    },
    onSuccess: () => {
      showToast('GRN rejected successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      setShowConfirmModal(false);
      setSelectedItem(null);
      setShowDetailsModal(false);
      setRejectionReason('');
    },
    onError: (error: any) => {
      const message = error?.statusCode === 403
        ? "Access Denied: You don't have permission to reject this GRN. Please contact your administrator if you need access."
        : (error?.message || 'Failed to reject GRN');
      showToast(message, 'error');
    },
  });

  // Approve Dispatch mutation
  const approveDispatchMutation = useMutation({
    mutationFn: async (dispatchId: number) => {
      const response = await api.post(`/dispatch/${dispatchId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      showToast('Dispatch approved successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setShowConfirmModal(false);
      setSelectedItem(null);
      setShowDetailsModal(false);
    },
    onError: (error: any) => {
      const message = error?.statusCode === 403
        ? "Access Denied: You don't have permission to approve this dispatch. Please contact your administrator if you need access."
        : (error?.message || 'Failed to approve dispatch');
      showToast(message, 'error');
    },
  });

  // Reject Dispatch mutation
  const rejectDispatchMutation = useMutation({
    mutationFn: async ({ dispatchId, rejectionReason }: { dispatchId: number; rejectionReason: string }) => {
      const response = await api.post(`/dispatch/${dispatchId}/reject`, { rejectionReason });
      return response.data;
    },
    onSuccess: () => {
      showToast('Dispatch rejected successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      setShowConfirmModal(false);
      setSelectedItem(null);
      setShowDetailsModal(false);
      setRejectionReason('');
    },
    onError: (error: any) => {
      const message = error?.statusCode === 403
        ? "Access Denied: You don't have permission to reject this dispatch. Please contact your administrator if you need access."
        : (error?.message || 'Failed to reject dispatch');
      showToast(message, 'error');
    },
  });

  const handleAction = (action: 'approve' | 'reject', item: any) => {
    setSelectedItem(item);
    setConfirmAction(action);
    setRejectionReason('');
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (!selectedItem || !confirmAction) return;

    if (selectedItem.grnNumber) {
      // It's a GRN
      if (confirmAction === 'approve') {
        approveMutation.mutate(selectedItem.id);
      } else {
        if (!rejectionReason.trim()) {
          showToast('Please provide a rejection reason', 'error');
          return;
        }
        rejectMutation.mutate({ grnId: selectedItem.id, rejectionReason });
      }
    } else {
      // It's a Dispatch
      if (confirmAction === 'approve') {
        approveDispatchMutation.mutate(selectedItem.id);
      } else {
        if (!rejectionReason.trim()) {
          showToast('Please provide a rejection reason', 'error');
          return;
        }
        rejectDispatchMutation.mutate({ dispatchId: selectedItem.id, rejectionReason });
      }
    }
  };

  // Fetch detailed items for selected GRN/Dispatch
  const { data: selectedItemDetails, isLoading: detailsLoading } = useQuery({
    queryKey: [selectedItem?.grnNumber ? 'grn' : 'dispatch', selectedItem?.id, 'items'],
    queryFn: async () => {
      if (!selectedItem) return null;
      if (selectedItem.grnNumber) {
        // Fetch GRN items
        const response = await api.get(`/grn/${selectedItem.id}/items`);
        return response.data;
      } else {
        // Fetch Dispatch items
        const response = await api.get(`/dispatch/${selectedItem.id}/items`);
        return response.data;
      }
    },
    enabled: !!selectedItem && showDetailsModal,
  });

  // Fetch zone details for better display (only when we have a selected item with warehouse ID)
  const { data: zonesData } = useQuery({
    queryKey: ['zones', selectedItem?.warehouseId],
    queryFn: async () => {
      if (!selectedItem?.warehouseId) return [];
      const response = await api.get(`/warehouses/${selectedItem.warehouseId}/zones`);
      // Handle different possible response structures
      if (response.data?.items) {
        return response.data.items;
      } else {
        return Array.isArray(response.data) ? response.data : [];
      }
    },
    enabled: !!selectedItem?.warehouseId && showDetailsModal,
  });

  // Helper function to get zone name from ID
  const getZoneName = (zoneId: number) => {
    if (!zonesData || !Array.isArray(zonesData)) return `Zone ${zoneId}`;
    const zone = zonesData.find((z: any) => z.id === zoneId);
    return zone ? (zone.code || zone.name || `Zone ${zoneId}`) : `Zone ${zoneId}`;
  };

  const handleViewDetails = (item: any) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  const inventoryColumns = [
    { 
      key: 'clientName', 
      label: 'Client', 
      width: '160px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <p className="text-sm font-semibold text-gray-900 truncate" title={value}>
            {value}
          </p>
        </div>
      ),
    },
    { 
      key: 'productDescription', 
      label: 'Product', 
      width: '200px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <p className="text-sm text-gray-900 truncate" title={value}>
            {value}
          </p>
        </div>
      ),
    },
    { 
      key: 'batchNo', 
      label: 'Batch No', 
      width: '120px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <p className="text-sm text-gray-700 truncate" title={value || 'N/A'}>
            {value || '-'}
          </p>
        </div>
      ),
    },
    {
      key: 'goodQty',
      label: 'Good',
      width: '100px',
      render: (value: number) => (
        <div className="flex justify-center items-center px-2">
          <span className="px-3 py-1 text-sm font-semibold text-green-700 bg-green-50 rounded-lg">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'cdamQty',
      label: 'CDAM',
      width: '100px',
      render: (value: number) => (
        <div className="flex justify-center items-center px-2">
          <span className="px-3 py-1 text-sm font-semibold text-yellow-700 bg-yellow-50 rounded-lg">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'damQty',
      label: 'DAM',
      width: '100px',
      render: (value: number) => (
        <div className="flex justify-center items-center px-2">
          <span className="px-3 py-1 text-sm font-semibold text-red-700 bg-red-50 rounded-lg">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'total',
      label: 'Total',
      width: '100px',
      render: (_: any, row: any) => {
        const total = (row.goodQty || 0) + (row.cdamQty || 0) + (row.damQty || 0);
        return (
          <div className="flex justify-center items-center px-2">
            <span className="px-3 py-1 text-sm font-bold text-gray-900 bg-gray-100 rounded-lg">
              {total}
            </span>
          </div>
        );
      },
    },
    {
      key: 'totalPallets',
      label: 'Pallets',
      width: '100px',
      render: (value: number) => (
        <div className="flex justify-center items-center px-2">
          <span className="px-3 py-1 text-sm font-semibold text-brand-700 bg-brand-50 rounded-lg">
            {value || 0}
          </span>
        </div>
      ),
    },
    {
      key: 'lastUpdated',
      label: 'Updated',
      width: '170px',
      render: (value: string) => (
        <div className="flex justify-center items-center text-sm text-gray-500 px-2">
          {formatDateTime(value)}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '100px',
      render: (_: any, row: any) => (
        <div className="flex justify-center items-center px-2">
          <button
            onClick={() => handleViewStorage(row)}
            className="group relative p-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-600 hover:text-white transition-all duration-200 hover:scale-110 hover:shadow-lg"
            title="View Storage Locations"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
              Storage
            </span>
          </button>
        </div>
      ),
    },
  ];

  // Discriminate a pending row's type for ACTION routing. Past GRN update
  // requests carry an explicit `type`; GRN/Dispatch are distinguished by which
  // number they have. (A reopened past GRN/dispatch is still a normal GRN/
  // dispatch here so it uses the normal approve/reject actions.)
  const getRowType = (
    row: any,
  ): 'grn' | 'dispatch' | 'past_grn_update' | 'past_dispatch_update' => {
    if (row?.type === 'past_grn_update') return 'past_grn_update';
    if (row?.type === 'past_dispatch_update') return 'past_dispatch_update';
    return row?.grnNumber ? 'grn' : 'dispatch';
  };

  // Type shown in the badge. A submitted GRN/dispatch that was reopened for a
  // past edit (`reopenedForEdit`) is badged PAST GRN / PAST DISPATCH so it isn't
  // mistaken for a fresh entry, even though it uses the normal approval actions.
  const getDisplayType = (row: any): keyof typeof TYPE_META => {
    const base = getRowType(row);
    if (base === 'grn' && row?.reopenedForEdit) return 'past_grn';
    if (base === 'dispatch' && row?.reopenedForEdit) return 'past_dispatch';
    return base;
  };

  const TYPE_META: Record<
    string,
    { label: string; variant: 'success' | 'warning' | 'secondary' | 'danger' }
  > = {
    grn: { label: 'GRN', variant: 'success' },
    dispatch: { label: 'DISPATCH', variant: 'warning' },
    past_grn_update: { label: 'PAST GRN', variant: 'secondary' },
    past_dispatch_update: { label: 'PAST DISPATCH', variant: 'danger' },
    past_grn: { label: 'PAST GRN', variant: 'secondary' },
    past_dispatch: { label: 'PAST DISPATCH', variant: 'danger' },
  };

  const pendingColumns = [
    {
      key: 'type',
      label: 'Type',
      width: '130px',
      render: (_: any, row: any) => {
        const meta = TYPE_META[getDisplayType(row)];
        return (
          <div className="flex justify-center items-center px-2">
            <Badge variant={meta.variant} rounded>
              {meta.label}
            </Badge>
          </div>
        );
      },
    },
    {
      key: 'number',
      label: 'Number',
      width: '150px',
      render: (_: any, row: any) => {
        const number = row.grnNumber || row.dispatchNumber;
        return (
          <div className="flex justify-center items-center px-2">
            <span className="text-sm font-medium text-gray-900 truncate" title={number || ''}>
              {number}
            </span>
          </div>
        );
      },
    },
    { 
      key: 'clientName', 
      label: 'Client', 
      width: '180px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <span className="text-sm text-gray-900 truncate" title={value || ''}>
            {value || '-'}
          </span>
        </div>
      ),
    },
    { 
      key: 'warehouseName', 
      label: 'Warehouse', 
      width: '160px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <span className="text-sm text-gray-700 truncate" title={value || ''}>
            {value || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Submitted',
      width: '170px',
      render: (value: string) => (
        <div className="flex justify-center items-center text-sm text-gray-500 px-2">
          {formatDateTime(value)}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '220px',
      render: (_: any, row: any) => {
        const rowType = getRowType(row);
        const isPastGrn = rowType === 'past_grn_update';
        const isPastDispatch = rowType === 'past_dispatch_update';
        const isPast = isPastGrn || isPastDispatch;
        // A reopened Past GRN/Dispatch (normal submitted row flagged
        // reopenedForEdit) is hardcoded to Admin / Super Admin only.
        const isReopenedPast = !isPast && Boolean(row.reopenedForEdit);
        // Past GRN/Dispatch approvals are hardcoded to Admin / Super Admin, and
        // only actionable while the request is still pending (history is view-only).
        const canApproveRow = isPast
          ? isAdmin && row.status === 'pending'
          : isReopenedPast
            ? isAdmin
            : rowType === 'grn'
              ? canApproveGrn
              : canApproveDispatch;
        const openPast = (act: 'view' | 'approve' | 'reject') => {
          if (isPastGrn) {
            setPastGrnUpdateId(row.id);
            setPastGrnAction(act);
          } else {
            setPastDispatchUpdateId(row.id);
            setPastDispatchAction(act);
          }
        };
        return (
          <div className="flex justify-center items-center space-x-2 px-2">
            <button
              onClick={() => (isPast ? openPast('view') : handleViewDetails(row))}
              className="text-brand-600 hover:text-brand-800 text-xs font-medium inline-flex items-center px-2 py-1 hover:bg-brand-50 rounded transition-colors"
            >
              <Eye className="h-3 w-3 mr-1" />
              View
            </button>
            {canApproveRow && (
              <>
                <button
                  onClick={() =>
                    isPast ? openPast('approve') : handleAction('approve', row)
                  }
                  disabled={approveMutation.isPending || rejectMutation.isPending || approveDispatchMutation.isPending || rejectDispatchMutation.isPending}
                  className="text-green-600 hover:text-green-800 text-xs font-medium inline-flex items-center disabled:opacity-50 px-2 py-1 hover:bg-green-50 rounded transition-colors"
                >
                  <Check className="h-3 w-3 mr-1" />
                  Approve
                </button>
                <button
                  onClick={() =>
                    isPast ? openPast('reject') : handleAction('reject', row)
                  }
                  disabled={approveMutation.isPending || rejectMutation.isPending || approveDispatchMutation.isPending || rejectDispatchMutation.isPending}
                  className="text-red-600 hover:text-red-800 text-xs font-medium inline-flex items-center disabled:opacity-50 px-2 py-1 hover:bg-red-50 rounded transition-colors"
                >
                  <X className="h-3 w-3 mr-1" />
                  Reject
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const ledgerColumns = [
    {
      key: 'createdAt',
      label: 'Date',
      width: '170px',
      render: (value: string) => (
        <div className="flex justify-center items-center text-sm text-gray-900 px-2">
          {formatDateTime(value)}
        </div>
      ),
    },
    { 
      key: 'clientName', 
      label: 'Client',
      width: '160px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <span className="text-sm text-gray-900 truncate" title={value || ''}>
            {value || '-'}
          </span>
        </div>
      ),
    },
    { 
      key: 'productDescription', 
      label: 'Product',
      width: '200px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <span className="text-sm text-gray-900 truncate" title={value || ''}>
            {value || '-'}
          </span>
        </div>
      ),
    },
    { 
      key: 'batchNo', 
      label: 'Batch',
      width: '120px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <span className="text-sm text-gray-700 truncate" title={value || ''}>
            {value || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'transactionType',
      label: 'Type',
      width: '110px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <Badge variant={value?.toLowerCase() === 'inward' ? 'success' : 'warning'} rounded>
            {value?.toUpperCase()}
          </Badge>
        </div>
      ),
    },
    {
      key: 'reference',
      label: 'Reference',
      width: '140px',
      render: (_: any, row: any) => (
        <div className="flex justify-center items-center px-2">
          <span className="text-sm font-mono text-gray-700">
            {row.referenceType}-{row.referenceId}
          </span>
        </div>
      ),
    },
    {
      key: 'goodQty',
      label: 'Good',
      width: '100px',
      render: (value: number, row: any) => {
        const isInward = row.transactionType?.toLowerCase() === 'inward';
        const displayValue = Math.abs(value);
        return (
          <div className={`flex justify-center items-center font-semibold px-2 ${isInward ? 'text-green-600' : 'text-red-600'}`}>
            {isInward ? '+' : '-'}{displayValue}
          </div>
        );
      },
    },
    {
      key: 'cdamQty',
      label: 'CDAM',
      width: '100px',
      render: (value: number, row: any) => {
        const isInward = row.transactionType?.toLowerCase() === 'inward';
        const displayValue = Math.abs(value);
        return (
          <div className={`flex justify-center items-center font-semibold px-2 ${isInward ? 'text-green-600' : 'text-red-600'}`}>
            {isInward ? '+' : '-'}{displayValue}
          </div>
        );
      },
    },
    {
      key: 'damQty',
      label: 'DAM',
      width: '100px',
      render: (value: number, row: any) => {
        const isInward = row.transactionType?.toLowerCase() === 'inward';
        const displayValue = Math.abs(value);
        return (
          <div className={`flex justify-center items-center font-semibold px-2 ${isInward ? 'text-green-600' : 'text-red-600'}`}>
            {isInward ? '+' : '-'}{displayValue}
          </div>
        );
      },
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen px-3 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
        {/* Header with enhanced shadow and gradient */}
        <div className="mb-4">
          <div className="flex items-center space-x-3 bg-gradient-to-r from-brand-100 to-brand-50 p-4 sm:p-5 rounded-xl shadow-lg border-2 border-brand-300">
            <div className="p-2.5 sm:p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md flex-shrink-0">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Inventory</h2>
              <p className="text-gray-600 mt-0.5 sm:mt-1 text-sm sm:text-base">Manage warehouse inventory and transaction ledger</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation — stack on phones so labels are never clipped */}
        <div className="mb-4 bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden">
          <div className="p-3 sm:p-5 bg-gradient-to-r from-gray-50 to-white">
            <nav className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <button
                onClick={() => navigate('/inventory/inventory')}
                className={`w-full min-h-[44px] py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                  activeTab === 'inventory'
                    ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-lg shadow-blue-200'
                    : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-brand-300 hover:shadow-md'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span>Approved Inventory</span>
              </button>
              <button
                onClick={() => navigate('/inventory/ledger')}
                className={`w-full min-h-[44px] py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                  activeTab === 'ledger'
                    ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-lg shadow-blue-200'
                    : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-brand-300 hover:shadow-md'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>Transaction Ledger</span>
              </button>
              {canAccessPendingApprovals && (
                <button
                  onClick={() => navigate('/inventory/pending')}
                  className={`w-full min-h-[44px] py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 sm:col-span-2 lg:col-span-1 ${
                    activeTab === 'pending'
                      ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-lg shadow-blue-200'
                      : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-brand-300 hover:shadow-md'
                  }`}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Pending Approvals</span>
                </button>
              )}
            </nav>
          </div>
        </div>

        {activeTab === 'inventory' && (
          <>
            <div className="bg-gradient-to-r from-brand-50 to-brand-100 border-2 border-brand-300 rounded-xl p-4 mb-4 shadow-md">
              <div className="flex items-center">
                <div className="p-2 bg-brand-200 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-brand-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-brand-800 text-sm font-medium">
                  This inventory reflects only approved GRN and Dispatch transactions.
                  Pending submissions are shown in the "Pending Approvals" tab.
                </p>
              </div>
            </div>

            {/* Search and Filter Controls */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden mb-4">
              <div className="px-3 py-3 lg:p-5">
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 sm:gap-4">
                  {/* Search Bar on Left */}
                  <div className="w-full sm:flex-1 sm:min-w-[200px] sm:max-w-md min-w-0">
                    {inventorySearchOpen ? (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Inventory</label>
                        <DebouncedSearchInput
                          placeholder="Search products, batches, clients..."
                          onSearch={setSearch}
                          value={search}
                          debounceMs={400}
                          className="w-full"
                          onClose={() => { setInventorySearchOpen(false); setSearch(''); }}
                        />
                      </>
                    ) : (
                      <button
                        onClick={() => setInventorySearchOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Search Inventory
                      </button>
                    )}
                  </div>

                  {/* Filter Button on Right */}
                  <div className="flex items-end">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg ${
                        showFilters
                          ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-blue-200'
                          : 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-700 border-2 border-gray-300 hover:border-brand-300'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                      <svg
                        className={`w-4 h-4 transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Collapsible Filter Fields */}
                {showFilters && (
                  <div className="mt-6 pt-6 border-t border-gray-200 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                        <ResponsiveSelect
                          value={clientFilter ?? ''}
                          onChange={(val) => setClientFilter(val ? parseInt(val) : undefined)}
                          placeholder="All Clients"
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                          options={(clientsData?.data ?? []).map((client: Client) => ({ value: client.id, label: client.companyName }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                        <ResponsiveSelect
                          value={categoryFilter || ''}
                          onChange={(val) => setCategoryFilter(val ? parseInt(val) : undefined)}
                          placeholder="All Categories"
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                          options={(categoriesData?.data?.items ?? []).map((category: any) => ({ value: category.id, label: category.name }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Batch No</label>
                        <input
                          type="text"
                          value={batchNoFilter}
                          onChange={(e) => setBatchNoFilter(e.target.value)}
                          placeholder="Enter batch number"
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
                        <ResponsiveSelect
                          value={sortOrder}
                          onChange={(val) => setSortOrder(val as 'ASC' | 'DESC')}
                          includeEmpty={false}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                          options={[
                            { value: 'DESC', label: 'Newest First' },
                            { value: 'ASC', label: 'Oldest First' },
                          ]}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button variant="secondary" onClick={clearInventoryFilters} fullWidth>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Clear Filters
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {isError ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-800">{(error as any)?.message || 'Failed to load inventory'}</p>
              </div>
            ) : (
              <ResponsiveTable
                columns={inventoryColumns}
                data={inventoryItems}
                isLoading={isLoading || isFetchingInventory}
                onLoadMore={() => fetchNextInventory()}
                hasMore={!!hasNextInventory}
                height="calc(100vh - 450px)"
                emptyMessage="No inventory items found"
              />
            )}
          </>
        )}

        {activeTab === 'ledger' && (
          <>
            {/* Search and Filter Controls */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden mb-4">
              <div className="px-3 py-3 lg:p-5">
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 sm:gap-4">
                  {/* Search Bar on Left */}
                  <div className="w-full sm:flex-1 sm:min-w-[200px] sm:max-w-md min-w-0">
                    {ledgerSearchOpen ? (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Transactions</label>
                        <DebouncedSearchInput
                          placeholder="Search transactions, clients, products..."
                          onSearch={setLedgerSearch}
                          value={ledgerSearch}
                          debounceMs={400}
                          className="w-full"
                          onClose={() => { setLedgerSearchOpen(false); setLedgerSearch(''); }}
                        />
                      </>
                    ) : (
                      <button
                        onClick={() => setLedgerSearchOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Search Transactions
                      </button>
                    )}
                  </div>

                  {/* Filter Button on Right */}
                  <div className="flex items-end">
                    <button
                      onClick={() => setShowLedgerFilters(!showLedgerFilters)}
                      className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg ${
                        showLedgerFilters
                          ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-blue-200'
                          : 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-700 border-2 border-gray-300 hover:border-brand-300'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <span>{showLedgerFilters ? 'Hide Filters' : 'Show Filters'}</span>
                      <svg 
                        className={`w-4 h-4 transition-transform duration-300 ${showLedgerFilters ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Collapsible Filter Section */}
            {showLedgerFilters && (
              <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden mb-4 transition-all duration-300">
                <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
                  </div>
                </div>
                <div className="px-1 py-3 lg:p-5">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                      <ResponsiveSelect
                        value={ledgerClientFilter ?? ''}
                        onChange={(val) => setLedgerClientFilter(val ? parseInt(val) : undefined)}
                        placeholder="All Clients"
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        options={(clientsData?.data ?? []).map((client: Client) => ({ value: client.id, label: client.companyName }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                      <ResponsiveSelect
                        value={ledgerTransactionType || ''}
                        onChange={(val) => setLedgerTransactionType(val as any || undefined)}
                        placeholder="All"
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        options={[
                          { value: 'inward', label: 'Inbound' },
                          { value: 'outward', label: 'Outbound' },
                        ]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={ledgerStartDate}
                        onChange={(e) => setLedgerStartDate(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={ledgerEndDate}
                        onChange={(e) => setLedgerEndDate(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button variant="secondary" onClick={clearLedgerFilters} fullWidth>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear All
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {transactionsError ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-800">Failed to load transactions</p>
              </div>
            ) : (
              <ResponsiveTable
                columns={ledgerColumns}
                data={transactionItems}
                isLoading={transactionsLoading || isFetchingTransactions}
                onLoadMore={() => fetchNextTransactions()}
                hasMore={!!hasNextTransactions}
                height="calc(100vh - 400px)"
                emptyMessage="No transactions found"
              />
            )}
          </>
        )}

        {activeTab === 'pending' && (
          <>
            {/* Search and Filter Controls */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden mb-4">
              <div className="px-1 py-3 lg:p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  {/* Search Bar on Left */}
                  <div className="flex-1 min-w-[200px] max-w-md">
                    {pendingSearchOpen ? (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Pending Items</label>
                        <DebouncedSearchInput
                          placeholder="Search GRNs, dispatches, clients..."
                          onSearch={setPendingSearch}
                          value={pendingSearch}
                          debounceMs={400}
                          className="w-full"
                          onClose={() => { setPendingSearchOpen(false); setPendingSearch(''); }}
                        />
                      </>
                    ) : (
                      <button
                        onClick={() => setPendingSearchOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Search Pending Items
                      </button>
                    )}
                  </div>

                  {/* Filter Button on Right */}
                  <div className="flex items-end">
                    <button
                      onClick={() => setShowPendingFilters(!showPendingFilters)}
                      className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg ${
                        showPendingFilters
                          ? 'bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-blue-200'
                          : 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-700 border-2 border-gray-300 hover:border-brand-300'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <span>{showPendingFilters ? 'Hide Filters' : 'Show Filters'}</span>
                      <svg 
                        className={`w-4 h-4 transition-transform duration-300 ${showPendingFilters ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Collapsible Filter Section */}
            {showPendingFilters && (
              <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden mb-4 transition-all duration-300">
                <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
                  </div>
                </div>
                <div className="px-1 py-3 lg:p-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                      <ResponsiveSelect
                        value={pendingType}
                        onChange={(val) => setPendingType(val as 'grn' | 'dispatch' | 'all' | 'pastgrn' | 'pastdispatch')}
                        includeEmpty={false}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        options={[
                          { value: 'all', label: 'All' },
                          { value: 'grn', label: 'GRN Only' },
                          { value: 'dispatch', label: 'Dispatch Only' },
                          // Past GRN / Past Dispatch: visible only to Admin / Super Admin
                          ...(isAdmin ? [
                            { value: 'pastgrn', label: 'Past GRN' },
                            { value: 'pastdispatch', label: 'Past Dispatch' },
                          ] : []),
                        ]}
                      />
                    </div>
                    {/* Status sub-filter for Past GRN — pending queue vs approved/rejected history */}
                    {pendingType === 'pastgrn' && isAdmin && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Request Status</label>
                        <select
                          value={pastGrnStatus}
                          onChange={(e) => setPastGrnStatus(e.target.value as 'pending' | 'approved' | 'rejected')}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                    )}
                    {/* Status sub-filter for Past Dispatch — pending queue vs approved/rejected history */}
                    {pendingType === 'pastdispatch' && isAdmin && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Request Status</label>
                        <select
                          value={pastDispatchStatus}
                          onChange={(e) => setPastDispatchStatus(e.target.value as 'pending' | 'approved' | 'rejected')}
                          className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                      <ResponsiveSelect
                        value={pendingClientFilter ?? ''}
                        onChange={(val) => setPendingClientFilter(val ? parseInt(val) : undefined)}
                        placeholder="All Clients"
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                        options={(clientsData?.data ?? []).map((client: Client) => ({ value: client.id, label: client.companyName }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={pendingStartDate}
                        onChange={(e) => setPendingStartDate(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={pendingEndDate}
                        onChange={(e) => setPendingEndDate(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button variant="secondary" onClick={clearPendingFilters} fullWidth>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear All
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(pendingGrnsLoading || pendingDispatchesLoading) && (
              <div className="flex justify-center py-12">
                <Spinner size="lg" />
              </div>
            )}

            {!pendingGrnsLoading && !pendingDispatchesLoading && (
              <>
                {/* Combine GRNs and Dispatches */}
                {(() => {
                  const combinedData = [];

                  if (pendingType === 'grn' || pendingType === 'all') {
                    combinedData.push(...(pendingGrns || []));
                  }

                  if (pendingType === 'dispatch' || pendingType === 'all') {
                    combinedData.push(...(pendingDispatches || []));
                  }

                  // Past GRN updates — Admin/Super Admin only; shown in "all" and "pastgrn".
                  if ((pendingType === 'pastgrn' || pendingType === 'all') && isAdmin) {
                    combinedData.push(...pendingPastGrn);
                  }

                  // Past Dispatch updates — Admin/Super Admin only; shown in "all" and "pastdispatch".
                  if ((pendingType === 'pastdispatch' || pendingType === 'all') && isAdmin) {
                    combinedData.push(...pendingPastDispatch);
                  }

                  // A reopened Past GRN/Dispatch (reopenedForEdit) is approvable
                  // by Admin / Super Admin only — hide those rows from everyone
                  // else so they never see the approval request.
                  const visibleData = isAdmin
                    ? combinedData
                    : combinedData.filter((r) => !r.reopenedForEdit);

                  // Sort by creation date (newest first)
                  visibleData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                  return (
                    <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden">
                      <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg className="w-6 h-6 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                Pending Approvals ({visibleData.length})
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                Items awaiting approval before inventory updates
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {visibleData.length > 0 ? (
                        <div className="px-1 py-3 lg:p-5">
                          <ResponsiveTable
                            columns={pendingColumns}
                            data={visibleData}
                            isLoading={isFetchingGrns || isFetchingDispatches}
                            onLoadMore={handlePendingLoadMore}
                            hasMore={!!hasNextGrns || !!hasNextDispatches}
                            height="calc(100vh - 450px)"
                            emptyMessage="No pending approvals"
                          />
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-600 font-semibold">No items pending approval</p>
                          <p className="text-sm text-gray-400 mt-1">All submissions have been processed</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </>
        )}

        <StorageSummaryModal
          isOpen={!!storageSummaryFilters}
          onClose={() => setStorageSummaryFilters(null)}
          filters={storageSummaryFilters}
        />

        {/* Past GRN update view / approve / reject (unified pending table) */}
        {pastGrnUpdateId && pastGrnAction && (
          <PastGrnUpdateModals
            updateId={pastGrnUpdateId}
            action={pastGrnAction}
            onClose={() => {
              setPastGrnUpdateId(null);
              setPastGrnAction(null);
            }}
          />
        )}

        {/* Past Dispatch update view / approve / reject (unified pending table) */}
        {pastDispatchUpdateId && pastDispatchAction && (
          <PastDispatchUpdateModals
            updateId={pastDispatchUpdateId}
            action={pastDispatchAction}
            onClose={() => {
              setPastDispatchUpdateId(null);
              setPastDispatchAction(null);
            }}
          />
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedItem && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-end justify-center p-3 lg:p-0 lg:block">
            <div className="relative lg:top-2 mx-auto p-6 border w-full lg:w-[95vw] max-w-7xl shadow-lg rounded-2xl lg:rounded-md bg-white max-h-[85vh] lg:max-h-[96vh] overflow-y-auto animate-slide-up lg:animate-none">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedItem.grnNumber ? 'GRN' : 'Dispatch'} Details - {selectedItem.grnNumber || selectedItem.dispatchNumber}
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700">
                        {selectedItem.grnNumber ? 'GRN Number' : 'Dispatch Number'}
                      </label>
                      <p className="mt-1 text-sm text-gray-900 font-semibold break-words">
                        {selectedItem.grnNumber || selectedItem.dispatchNumber}
                      </p>
                    </div>
                    {selectedItem.grnNumber && selectedItem.gateEntryNumber && (
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700">Gate Entry Number</label>
                        <p className="mt-1 text-sm text-gray-900 break-words">{selectedItem.gateEntryNumber}</p>
                      </div>
                    )}
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700">Client</label>
                      <p className="mt-1 text-sm text-gray-900 break-words">{selectedItem.clientName}</p>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700">Warehouse</label>
                      <p className="mt-1 text-sm text-gray-900 break-words">{selectedItem.warehouseName}</p>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className="mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        SUBMITTED
                      </span>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700">Created By</label>
                      <p className="mt-1 text-sm text-gray-900 break-words">{selectedItem.createdByName}</p>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700">Created At</label>
                      <p className="mt-1 text-sm text-gray-900 break-words">
                        {formatDateTime(selectedItem.createdAt)}
                      </p>
                    </div>
                    {/* Dispatch specific fields - Destination and Remarks */}
                    {!selectedItem.grnNumber && selectedItem.destination && (
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700">Destination</label>
                        <p className="mt-1 text-sm text-gray-900 break-words">{selectedItem.destination}</p>
                      </div>
                    )}
                    {!selectedItem.grnNumber && selectedItem.remarks && (
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700">Dispatch Remarks</label>
                        <p className="mt-1 text-sm text-gray-900 break-words">{selectedItem.remarks}</p>
                      </div>
                    )}
                    {/* GRN specific field - Client Location */}
                    {selectedItem.grnNumber && selectedItem.clientLocation && (
                      <div className="min-w-0">
                        <label className="block text-sm font-medium text-gray-700">Client Location</label>
                        <p className="mt-1 text-sm text-gray-900 break-all">{selectedItem.clientLocation}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Items Section */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    {selectedItem.grnNumber ? 'GRN Items' : 'Dispatch Items'} ({selectedItemDetails?.length || 0})
                  </h4>

                  {detailsLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
                    </div>
                  ) : selectedItemDetails && selectedItemDetails.length > 0 ? (
                    <div className="space-y-4">
                      {selectedItemDetails.map((item: any, index: number) => (
                        <div key={item.id || index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          {/* Item Header */}
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h5 className="text-lg font-medium text-gray-900">{item.productDescription}</h5>
                              <p className="text-sm text-gray-600">{item.productCategoryName}</p>
                            </div>
                            <div className="text-right">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                                Item #{index + 1}
                              </span>
                            </div>
                          </div>

                          {/* Item Details Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
                            {selectedItem.grnNumber && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">SKU Code</label>
                                  <p className="mt-1 text-sm font-semibold text-gray-900">{item.skuCode || '-'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">SAP Code</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.customerSapCode || '-'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Item Code</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.itemCode || '-'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Batch No</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.batchNo}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Unit Type</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.unitType}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">MFG Date</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.mfgDate ? new Date(item.mfgDate).toLocaleString() : '-'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Expiry Date</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.expiryDate ? new Date(item.expiryDate).toLocaleString() : '-'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Good Qty</label>
                                  <p className="mt-1 text-sm font-semibold text-green-600">{item.goodQty}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">CDAM Qty</label>
                                  <p className="mt-1 text-sm font-semibold text-yellow-600">{item.cdamQty}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">DAM Qty</label>
                                  <p className="mt-1 text-sm font-semibold text-red-600">{item.damQty}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Pallets</label>
                                  <p className="mt-1 text-sm font-semibold text-brand-600">{item.palletCount}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Weight/Item</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.weightPerItem || '-'}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Total Weight</label>
                                  <p className="mt-1 text-sm font-semibold text-purple-600">{item.totalWeight}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Storage Type</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.storageType}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Short/Excess</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.shortExcess || '-'}</p>
                                </div>
                              </>
                            )}
                            {!selectedItem.grnNumber && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Batch No</label>
                                  <p className="mt-1 text-sm text-gray-900">{item.batchNo}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Good Qty Out</label>
                                  <p className="mt-1 text-sm font-semibold text-green-600">{item.goodQty}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">CDAM Qty Out</label>
                                  <p className="mt-1 text-sm font-semibold text-yellow-600">{item.cdamQty}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">DAM Qty Out</label>
                                  <p className="mt-1 text-sm font-semibold text-red-600">{item.damQty}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Pallets Out</label>
                                  <p className="mt-1 text-sm font-semibold text-brand-600">{item.palletCount}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Available Good</label>
                                  <p className="mt-1 text-sm text-gray-600">{item.availableGoodQty}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Available CDAM</label>
                                  <p className="mt-1 text-sm text-gray-600">{item.availableCdamQty}</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase">Available DAM</label>
                                  <p className="mt-1 text-sm text-gray-600">{item.availableDamQty}</p>
                                </div>
                              </>
                            )}
                          </div>

                            {/* Storage Locations Section */}
                            <div className="border-t border-gray-200 pt-4">
                              <h6 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Storage Locations
                              </h6>
                              
                              {/* For GRN items */}
                              {selectedItem.grnNumber && item.storageDetails && item.storageDetails.length > 0 ? (
                                <div className="space-y-3">
                                  {item.storageDetails.map((storage: any, idx: number) => (
                                    <div key={idx} className="bg-gray-50 rounded-lg p-3">
                                      <div className="text-sm font-medium text-gray-800 mb-2 flex items-center">
                                        <svg className="h-3 w-3 mr-1 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 104 0 2 2 0 00-4 0zm6 0a2 2 0 104 0 2 2 0 00-4 0z" clipRule="evenodd" />
                                        </svg>
                                        {storage.zoneLocationCode || getZoneName(storage.zoneId)}
                                      </div>
                                      <div className="bg-brand-50 border-brand-400 p-2 rounded border-l-4">
                                        <div className="flex justify-between items-center">
                                          <div>
                                            <p className="text-sm font-medium text-gray-900">
                                              {item.storageType === 'ground' ? 'Ground Storage' :
                                               storage.rackCode ? `Rack: ${storage.rackCode}` : 'Rack Storage'
                                              }
                                            </p>
                                            {item.storageType === 'rack' && storage.locationCode && (
                                              <p className="text-xs text-gray-600">
                                                Location: {storage.locationCode}
                                              </p>
                                            )}
                                            {item.storageType === 'ground' && (
                                              <p className="text-xs text-gray-600">
                                                {storage.zoneLocationCode ? `Slot: ${storage.zoneLocationCode}` : 'Zone-based storage'}
                                              </p>
                                            )}
                                          </div>
                                          <div className="text-right">
                                            <p className="text-sm font-semibold text-gray-900">
                                              {storage.pallets || item.palletCount || 1} pallets
                                            </p>
                                            {item.storageType === 'rack' && storage.rackCode && storage.locationCode && (
                                              <p className="text-xs text-gray-500">
                                                {storage.rackCode}-{storage.locationCode}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : 
                              /* For Dispatch items */
                              !selectedItem.grnNumber && item.storageSelections && item.storageSelections.length > 0 ? (
                                <div className="space-y-3">
                                  {item.storageSelections.map((storage: any, idx: number) => (
                                    <div key={idx} className="bg-gray-50 rounded-lg p-3">
                                      <div className="text-sm font-medium text-gray-800 mb-2 flex items-center">
                                        <svg className="h-3 w-3 mr-1 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 104 0 2 2 0 00-4 0zm6 0a2 2 0 104 0 2 2 0 00-4 0z" clipRule="evenodd" />
                                        </svg>
                                        Zone: {storage.zoneLocation || storage.zone || 'Unknown'}
                                      </div>
                                      <div className="bg-red-50 border-red-400 p-2 rounded border-l-4">
                                        <div className="flex justify-between items-center">
                                          <div>
                                            <p className="text-sm font-medium text-gray-900">
                                              {storage.rack ? `Rack: ${storage.rack}` : 'Ground Storage'}
                                            </p>
                                            {storage.location && (
                                              <p className="text-xs text-gray-600">
                                                Location: {storage.location}
                                              </p>
                                            )}
                                            {!storage.rack && !storage.location && (
                                              <p className="text-xs text-gray-600">
                                                Ground Storage
                                              </p>
                                            )}
                                          </div>
                                          <div className="text-right">
                                            <p className="text-sm font-semibold text-red-600">
                                              -{storage.palletsToDispatch} pallets
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              Outbound
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                  <div className="flex items-center">
                                    <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-sm text-yellow-800">No storage location details available</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* LBD Locations Section (damaged goods) - GRN items only */}
                            {selectedItem.grnNumber && ((item.cdamQty || 0) + (item.damQty || 0) > 0) && (
                              <div className="border-t border-gray-200 pt-4 mt-4">
                                <h6 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                  <svg className="h-4 w-4 mr-2 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  LBD Locations (Damaged Goods)
                                </h6>

                                {Array.isArray(item.lbdStorageDetails) &&
                                item.lbdStorageDetails.filter((p: any) => p && typeof p === 'object' && (typeof p.zoneId === 'number' || typeof p.rackId === 'number')).length > 0 ? (
                                  <div className="space-y-3">
                                    {item.lbdStorageDetails
                                      .filter((p: any) => p && typeof p === 'object' && (typeof p.zoneId === 'number' || typeof p.rackId === 'number'))
                                      .map((placement: any, idx: number) => {
                                        const isRack = typeof placement.rackId === 'number';
                                        return (
                                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                                          <div className="text-sm font-medium text-gray-800 mb-2 flex items-center">
                                            <svg className="h-3 w-3 mr-1 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 104 0 2 2 0 00-4 0zm6 0a2 2 0 104 0 2 2 0 00-4 0z" clipRule="evenodd" />
                                            </svg>
                                            {isRack
                                              ? (placement.rackCode || `Rack ${placement.rackId}`)
                                              : (placement.zoneLocationCode || placement.zoneCode || getZoneName(placement.zoneId))}
                                          </div>
                                          <div className="bg-amber-50 border-amber-400 p-2 rounded border-l-4">
                                            <div className="flex justify-between items-center">
                                              <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                  {item.lbdStorageMode === 'shared' ? 'Shared LBD Pallet' : 'Dedicated LBD Pallet'}
                                                </p>
                                                <p className="text-xs text-gray-600">
                                                  {item.lbdStorageMode === 'shared'
                                                    ? `Shared ref: ${item.lbdSharedPalletRef || placement.sharedPalletRef || '-'}`
                                                    : isRack
                                                      ? (placement.locationCode ? `Location: ${placement.locationCode}` : 'LBD rack storage')
                                                      : (placement.zoneLocationCode ? `Slot: ${placement.zoneLocationCode}` : 'LBD zone storage')}
                                                </p>
                                              </div>
                                              <div className="text-right">
                                                <p className="text-sm font-semibold text-amber-700">
                                                  {item.lbdStorageMode === 'shared' ? 'Shared' : `${placement.pallets ?? 0} pallets`}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        );
                                      })}
                                    {item.lbdStorageMode === 'dedicated' && item.lbdPalletCount != null && (
                                      <p className="text-xs text-gray-500">Total LBD pallets: {item.lbdPalletCount}</p>
                                    )}
                                  </div>
                                ) : (
                                  <div className="bg-amber-50 border-amber-400 p-3 rounded border-l-4">
                                    <p className="text-sm text-gray-700">
                                      No LBD location recorded. 1 LBD pallet will be auto-assigned in the warehouse's LBD zone on approval.
                                    </p>
                                  </div>
                                )}

                                {item.lbdRemark && (
                                  <p className="text-xs text-gray-500 mt-2">LBD Remark: {item.lbdRemark}</p>
                                )}
                              </div>
                            )}

                          {/* Remarks */}
                          {item.remarks && (
                            <div className="border-t border-gray-200 pt-3 mt-3">
                              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Remarks</label>
                              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{item.remarks}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No items found</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                {((selectedItem.grnNumber && canApproveGrn) ||
                  (!selectedItem.grnNumber && canApproveDispatch)) && (
                  <>
                    <button
                      onClick={() => handleAction('reject', selectedItem)}
                      disabled={approveMutation.isPending || rejectMutation.isPending || approveDispatchMutation.isPending || rejectDispatchMutation.isPending}
                      className="px-6 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => handleAction('approve', selectedItem)}
                      disabled={approveMutation.isPending || rejectMutation.isPending || approveDispatchMutation.isPending || rejectDispatchMutation.isPending}
                      className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && selectedItem && confirmAction && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-end justify-center p-3 lg:p-0 lg:block">
            <div className="relative lg:top-20 mx-auto p-5 border w-full lg:w-96 shadow-lg rounded-2xl lg:rounded-md bg-white max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
              <div className="mt-3 text-center">
                <h3 className="text-lg font-medium text-gray-900">
                  {confirmAction === 'approve' ? 'Approve' : 'Reject'} {selectedItem.grnNumber ? 'GRN' : 'Dispatch'}
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to {confirmAction} {selectedItem.grnNumber ? 'GRN' : 'Dispatch'} {selectedItem.grnNumber || selectedItem.dispatchNumber}?
                    {confirmAction === 'approve' && (
                      <span className="block mt-2 text-brand-600">
                        This will update inventory records and cannot be undone.
                      </span>
                    )}
                  </p>
                  {confirmAction === 'reject' && (
                    <div className="mt-4 text-left">
                      <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="rejectionReason"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Please provide a reason for rejection..."
                      />
                    </div>
                  )}
                </div>
                <div className="flex justify-center space-x-3 mt-4">
                  <button
                    onClick={() => {
                      setShowConfirmModal(false);
                      setRejectionReason('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={approveMutation.isPending || rejectMutation.isPending || approveDispatchMutation.isPending || rejectDispatchMutation.isPending}
                    className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 ${
                      confirmAction === 'approve'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {(approveMutation.isPending || rejectMutation.isPending || approveDispatchMutation.isPending || rejectDispatchMutation.isPending)
                      ? 'Processing...'
                      : confirmAction === 'approve'
                      ? 'Approve'
                      : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}